import type { CSSProperties } from "react";
import type { ModalBackdropConfig } from "@/contexts/layout/modal-stack-types";
import { ContainerPreset } from "@/constants/componentPresets";
import { ColorType } from "@/theme/colors";
import { pxToRem } from "@/theme/spacing";
import {
  CARD_STACK_GAP,
  CONTENT_GAP,
  CSS_FIT_CONTENT,
  CSS_FULL,
  DEFAULT_BACKDROP_ANIMATION_DURATION,
  DEFAULT_BACKDROP_BLUR,
  DEFAULT_BACKDROP_OPACITY,
  DEFAULT_CARD_WIDTH,
  DEFAULT_CONTAINER_BACKDROP_BLUR,
  DEFAULT_CONTAINER_PADDING_TOP,
  DEFAULT_INVISIMODAL_BACKDROP_BLUR,
  DEFAULT_INVISIMODAL_BACKDROP_OPACITY,
  DEFAULT_SCROLLABLE_PADDING,
  INVISIMODAL_CONTAINER_COLOR,
  INVISIMODAL_NO_BACKGROUND_IMAGE,
  INVISIMODAL_NO_FILTER,
  INVISIMODAL_NO_SHADOW,
  INVISIMODAL_OVERLAY_BACKGROUND_COLOR,
  INVISIMODAL_OVERLAY_BACKGROUND_IMAGE,
  INVISIMODAL_TRANSPARENT_BACKGROUND,
} from "./crisingmodalConstants";

interface RisingBackdropInput {
  backdropBlur?: number;
  backdropOpacity?: number;
  backdropUseGradient?: boolean;
  isInvisimodal: boolean;
}

interface ContainerModelInput {
  boxShadow: string;
  containerBackdropBlur?: number;
  containerBackgroundColor: ColorType;
  containerBackgroundOpacity: number;
  containerMarginTop?: string | number;
  containerPreset: ContainerPreset;
  containerStyle?: CSSProperties;
  isInvisimodal: boolean;
}

export interface CrisingmodalContainerModel {
  backdropBlur: number;
  backgroundColor: ColorType;
  backgroundOpacity: number;
  boxShadow: string;
  preset: ContainerPreset;
  style?: CSSProperties;
}

export interface CrisingmodalStyleModel {
  cardMotionStyle: CSSProperties;
  cardStackStyle: CSSProperties;
  contentStyle: CSSProperties;
  contentWrapperStyle: CSSProperties;
  overlayStyle: CSSProperties;
  scrollableStyle: CSSProperties;
}

export function buildRisingBackdrop({
  backdropBlur,
  backdropOpacity,
  backdropUseGradient,
  isInvisimodal,
}: RisingBackdropInput): ModalBackdropConfig {
  const effectiveBackdropUseGradient = backdropUseGradient ?? true;

  return {
    opacity: backdropOpacity ??
      (isInvisimodal
        ? DEFAULT_INVISIMODAL_BACKDROP_OPACITY
        : DEFAULT_BACKDROP_OPACITY),
    blur: backdropBlur ??
      (isInvisimodal
        ? DEFAULT_INVISIMODAL_BACKDROP_BLUR
        : DEFAULT_BACKDROP_BLUR),
    useGradient: isInvisimodal ? false : effectiveBackdropUseGradient,
    animationDuration: DEFAULT_BACKDROP_ANIMATION_DURATION,
  };
}

export function buildContainerModel({
  boxShadow,
  containerBackdropBlur,
  containerBackgroundColor,
  containerBackgroundOpacity,
  containerMarginTop,
  containerPreset,
  containerStyle,
  isInvisimodal,
}: ContainerModelInput): CrisingmodalContainerModel {
  return {
    backdropBlur: isInvisimodal
      ? 0
      : containerBackdropBlur ?? DEFAULT_CONTAINER_BACKDROP_BLUR,
    backgroundColor: isInvisimodal
      ? INVISIMODAL_CONTAINER_COLOR
      : containerBackgroundColor,
    backgroundOpacity: isInvisimodal ? 0 : containerBackgroundOpacity,
    boxShadow: isInvisimodal ? INVISIMODAL_NO_SHADOW : boxShadow,
    preset: isInvisimodal ? ContainerPreset.Default : containerPreset,
    style: buildContainerStyle({
      containerMarginTop,
      containerStyle,
      isInvisimodal,
    }),
  };
}

function buildContainerStyle(input: {
  containerMarginTop?: string | number;
  containerStyle?: CSSProperties;
  isInvisimodal: boolean;
}): CSSProperties | undefined {
  if (
    input.containerMarginTop == null &&
    !input.containerStyle &&
    !input.isInvisimodal
  ) {
    return undefined;
  }

  return {
    ...(input.containerMarginTop != null
      ? { marginTop: input.containerMarginTop }
      : {}),
    ...input.containerStyle,
    ...(input.isInvisimodal
      ? {
          backgroundColor: INVISIMODAL_TRANSPARENT_BACKGROUND,
          backgroundImage: INVISIMODAL_NO_BACKGROUND_IMAGE,
          boxShadow: INVISIMODAL_NO_SHADOW,
          backdropFilter: INVISIMODAL_NO_FILTER,
          WebkitBackdropFilter: INVISIMODAL_NO_FILTER,
        }
      : {}),
  };
}

export function buildCrisingmodalStyleModel(input: {
  cardWidth: string;
  contentStyle?: CSSProperties;
  contentWrapperStyle?: CSSProperties;
  isInvisimodal: boolean;
  registrationContentZIndex: number;
  scrollableStyleOverride?: CSSProperties;
  stackMarginTop?: string | number;
}): CrisingmodalStyleModel {
  return {
    cardMotionStyle: {
      width: input.cardWidth,
      pointerEvents: "auto",
    },
    cardStackStyle: {
      display: "flex",
      flexDirection: "column",
      gap: pxToRem(CARD_STACK_GAP),
      ...(input.stackMarginTop == null ? {} : { marginTop: input.stackMarginTop }),
    },
    contentStyle: {
      display: "flex",
      flexDirection: "column",
      width: CSS_FULL,
      gap: pxToRem(CONTENT_GAP),
      position: "relative",
      ...input.contentStyle,
    },
    contentWrapperStyle: {
      position: "relative",
      padding: `${DEFAULT_CONTAINER_PADDING_TOP} 0 0`,
      ...input.contentWrapperStyle,
    },
    overlayStyle: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: input.registrationContentZIndex,
      display: "flex",
      flexDirection: "column",
      height: CSS_FULL,
      ...(input.isInvisimodal
        ? {
            backgroundColor: INVISIMODAL_OVERLAY_BACKGROUND_COLOR,
            backgroundImage: INVISIMODAL_OVERLAY_BACKGROUND_IMAGE,
          }
        : {}),
    },
    scrollableStyle: {
      width: CSS_FULL,
      height: CSS_FULL,
      justifyContent: "flex-start",
      alignItems: "center",
      boxSizing: "border-box",
      padding: DEFAULT_SCROLLABLE_PADDING,
      ...input.scrollableStyleOverride,
    },
  };
}

export function getCardWidth(input: {
  isMobile: boolean;
}): string {
  return input.isMobile ? "92%" : pxToRem(DEFAULT_CARD_WIDTH);
}

export function getBackButtonStyle(input: {
  backButtonInset: string | number;
}): CSSProperties {
  return {
    display: "flex",
    justifyContent: "flex-end",
    width: CSS_FIT_CONTENT,
    position: "absolute",
    top: input.backButtonInset,
    right: input.backButtonInset,
  };
}
